// ─── CLIENTES ────────────────────────────────────────────────────────────────
// Catálogo de clientes: alta, edición y búsqueda. El backend (/api/clientes)
// guarda la lista completa del usuario, igual que /cxp-facturas.
// La cartera (saldo, vencido, facturas) viene del sistema Estrategia vía
// /api/clientes/importar-estrategia y aquí es de solo lectura. El vendedor se
// captura a mano o con "Importar de Excel" (Estrategia también lo puede llenar).
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { api } from "../auth";

const CONDICIONES = [
  { id: "", label: "— Sin capturar —" },
  { id: "contado", label: "Contado" },
  { id: "15", label: "15 días" },
  { id: "30", label: "30 días" },
  { id: "60", label: "60 días" },
  { id: "90", label: "90 días" },
  { id: "120", label: "120 días" },
];
const condicionLabel = (id) => (id ? CONDICIONES.find((c) => c.id === id)?.label || "—" : "—");

const VACIO = {
  clave: "",
  nombreComercial: "",
  razonSocial: "",
  rfc: "",
  contacto: "",
  telefono: "",
  correo: "",
  direccionEntrega: "",
  ciudad: "",
  estado: "",
  cp: "",
  vendedor: "",
  condicionesPago: "contado",
  limiteCredito: "",
  notas: "",
};

const mxn = (n) =>
  n === "" || n === null || n === undefined || !isFinite(Number(n))
    ? "—"
    : Number(n).toLocaleString("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });

// "2026-09-22" → "22/09/2026"; cualquier otro formato se muestra tal cual.
const fecha = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s || "—";
};
const num = (n) => (isFinite(Number(n)) ? Number(n) : 0);

const ORDENES = [
  { id: "saldo", label: "Saldo (mayor a menor)" },
  { id: "vencido", label: "Vencido (mayor a menor)" },
  { id: "atraso", label: "Días de atraso (mayor a menor)" },
  { id: "nombre", label: "Nombre (A-Z)" },
];

const nuevoId = () => `cli_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

// Quita acentos y pasa a minúsculas para que la búsqueda no dependa de ellos.
const normalizar = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const RFC_RE = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;

// ─── IMPORTAR DE EXCEL ───────────────────────────────────────────────────────
// Lee un libro con encabezados "Clave, Razón social, Vendedor, Contacto,
// Teléfono, Email, Ciudad, Estado, C.P., …" (ej. la hoja "Lista unificada").
// Usa la hoja "Lista unificada" si existe; si no, la primera que tenga la
// columna "Razón social". Los encabezados se buscan por nombre, no por posición.

// Correos de relleno que se repiten en muchos clientes; no se importan.
const CORREOS_GENERICOS = [
  "ventas@ventas.com",
  "ventas@gmail.com",
  "ventas@cotton.com",
  "cottonsilkalmacen@outlook.com",
  "almacentejidoslimon@gmail.com",
];

const fechaExcel = (v) => {
  if (v === "" || v === null || v === undefined) return "";
  const d = v instanceof Date ? v : typeof v === "number" ? new Date(Math.round((v - 25569) * 86400000)) : new Date(v);
  return isNaN(d) ? "" : d.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
};

const parseLibroClientes = (wb) => {
  const conHeader = (nombre) => {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[nombre], { header: 1, defval: "" });
    const idx = rows.findIndex((r) => r.some((c) => normalizar(c).trim() === "razon social"));
    return idx === -1 ? null : { rows, idx };
  };
  const hoja =
    wb.SheetNames.find((n) => normalizar(n).trim() === "lista unificada" && conHeader(n)) ||
    wb.SheetNames.find((n) => conHeader(n));
  if (!hoja) return [];
  const { rows, idx } = conHeader(hoja);
  const enc = rows[idx].map((c) => normalizar(c).trim());
  const col = (...nombres) => enc.findIndex((h) => nombres.includes(h));
  const C = {
    clave: col("clave"),
    razon: col("razon social"),
    vendedor: col("vendedor"),
    contacto: col("contacto"),
    telefono: col("telefono"),
    correo: col("email", "correo"),
    ciudad: col("ciudad"),
    estado: col("estado"),
    cp: col("c.p.", "cp", "codigo postal"),
    notaUbic: col("nota ubicacion"),
    origen: col("origen"),
    ultima: col("ultima compra", "ultima factura", "ultima remision"),
  };
  const txt = (r, i) => (i === -1 ? "" : String(r[i] ?? "").trim());
  const ahora = new Date().toISOString();
  return rows
    .slice(idx + 1)
    .filter((r) => txt(r, C.razon))
    .map((r) => {
      const razon = txt(r, C.razon);
      let contacto = txt(r, C.contacto);
      if (/^(mismo|misma)$/i.test(contacto)) contacto = razon;
      let correo = txt(r, C.correo).toLowerCase();
      if (CORREOS_GENERICOS.includes(correo)) correo = "";
      const notas = [
        txt(r, C.origen) && `Origen: ${txt(r, C.origen)}`,
        C.ultima !== -1 && fechaExcel(r[C.ultima]) && `Última compra: ${fechaExcel(r[C.ultima])}`,
        txt(r, C.notaUbic),
      ]
        .filter(Boolean)
        .join(" · ");
      return {
        ...VACIO,
        id: nuevoId(),
        clave: txt(r, C.clave),
        nombreComercial: razon,
        razonSocial: razon,
        contacto,
        telefono: txt(r, C.telefono),
        correo,
        ciudad: txt(r, C.ciudad),
        estado: txt(r, C.estado),
        cp: txt(r, C.cp),
        vendedor: txt(r, C.vendedor),
        notas,
        creado: ahora,
        actualizado: ahora,
      };
    });
};

// Junta los importados con la lista actual. Un cliente se considera el mismo si
// comparte alguna clave o la razón social. A los existentes solo se les llenan
// los campos vacíos (no se pisa nada capturado a mano).
const combinarClientes = (actuales, nuevos) => {
  const claves = (c) => String(c.clave || "").split("/").map((k) => normalizar(k).trim()).filter(Boolean);
  const lista = actuales.map((c) => ({ ...c }));
  let agregados = 0;
  let actualizados = 0;
  for (const n of nuevos) {
    const kn = claves(n);
    const rn = normalizar(n.razonSocial).trim();
    const previo = lista.find(
      (c) => claves(c).some((k) => kn.includes(k)) || (rn && normalizar(c.razonSocial).trim() === rn)
    );
    if (!previo) {
      lista.push(n);
      agregados++;
      continue;
    }
    let cambio = false;
    for (const campo of ["clave", "razonSocial", "contacto", "telefono", "correo", "ciudad", "estado", "cp", "vendedor", "notas"]) {
      if (!String(previo[campo] ?? "").trim() && n[campo]) {
        previo[campo] = n[campo];
        cambio = true;
      }
    }
    if (cambio) {
      previo.actualizado = n.actualizado;
      actualizados++;
    }
  }
  return { lista, agregados, actualizados };
};

// Suma de saldo y vencido de la lista que se le pase (factura o SL, nunca ambas).
function TotalesCartera({ lista, filtrado }) {
  const saldo = lista.reduce((s, c) => s + num(c.saldo), 0);
  const vencido = lista.reduce((s, c) => s + num(c.saldo_vencido), 0);
  const conSaldo = lista.filter((c) => num(c.saldo) > 0).length;
  return (
    <div className="flex gap-6 flex-wrap">
      <div className="text-right">
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
          Saldo total{filtrado ? " (búsqueda)" : ""}
        </p>
        <p className="text-lg font-bold text-slate-800" data-testid="clientes-total-saldo">{mxn(saldo)}</p>
        <p className="text-[11px] text-slate-500">{conSaldo} con saldo</p>
      </div>
      <div className="text-right">
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
          Vencido{filtrado ? " (búsqueda)" : ""}
        </p>
        <p className={`text-lg font-bold ${vencido > 0 ? "text-rose-600" : "text-slate-800"}`} data-testid="clientes-total-vencido">
          {mxn(vencido)}
        </p>
        {saldo > 0 && <p className="text-[11px] text-slate-500">{((vencido / saldo) * 100).toFixed(1)}% del saldo</p>}
      </div>
    </div>
  );
}

// Cartera de solo lectura (saldo, vencido, atraso y facturas pendientes) de un cliente de Estrategia.
function CarteraDetalle({ c, titulo = "Cartera según Estrategia" }) {
  const facturas = Array.isArray(c.facturas) ? c.facturas : [];
  const conDoc = facturas.some((f) => f.doc);
  return (
    <div className="border border-slate-200 rounded-xl p-3 space-y-2 bg-slate-50" data-testid="cliente-cartera">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-semibold text-slate-600">
          {titulo}
          {c.saldo_al ? ` · al ${fecha(c.saldo_al)}` : ""}
        </p>
        <p className="text-[11px] text-slate-500">
          Clave {c.id_estrategia}
          {c.vendedor ? ` · Vendedor: ${c.vendedor}` : ""}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-white rounded-lg border border-slate-200 p-2">
          <p className="text-[11px] text-slate-500">Saldo</p>
          <p className="text-sm font-bold text-slate-800">{mxn(c.saldo)}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-2">
          <p className="text-[11px] text-slate-500">Vencido</p>
          <p className={`text-sm font-bold ${num(c.saldo_vencido) > 0 ? "text-rose-600" : "text-slate-800"}`}>
            {mxn(c.saldo_vencido)}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-2">
          <p className="text-[11px] text-slate-500">Máx. días de atraso</p>
          <p className={`text-sm font-bold ${num(c.dias_atraso_max) > 0 ? "text-rose-600" : "text-slate-800"}`}>
            {num(c.dias_atraso_max)}
          </p>
        </div>
      </div>
      {facturas.length > 0 ? (
        <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
          <table className="w-full text-xs" data-testid="cliente-facturas">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                <th className="text-left px-2 py-1.5 font-semibold">Referencia</th>
                {conDoc && <th className="text-left px-2 py-1.5 font-semibold">Doc.</th>}
                <th className="text-left px-2 py-1.5 font-semibold">Fecha</th>
                <th className="text-left px-2 py-1.5 font-semibold">Vence</th>
                <th className="text-right px-2 py-1.5 font-semibold">Atraso</th>
                <th className="text-right px-2 py-1.5 font-semibold">Importe</th>
                <th className="text-right px-2 py-1.5 font-semibold">Abonos</th>
                <th className="text-right px-2 py-1.5 font-semibold">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {facturas.map((f, i) => (
                <tr key={`${f.referencia}-${i}`} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 font-mono">{f.referencia || "—"}</td>
                  {conDoc && <td className="px-2 py-1.5">{f.doc || "—"}</td>}
                  <td className="px-2 py-1.5 whitespace-nowrap">{fecha(f.fecha)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{fecha(f.vence)}</td>
                  <td className={`px-2 py-1.5 text-right ${num(f.atraso) > 0 ? "text-rose-600 font-semibold" : ""}`}>
                    {num(f.atraso) > 0 ? `${num(f.atraso)} d` : "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">{mxn(f.importe)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">{mxn(f.abonos)}</td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap font-semibold">{mxn(f.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-slate-500">Sin facturas pendientes.</p>
      )}
      <p className="text-[11px] text-slate-400">Estos datos se actualizan solos desde Estrategia; no se editan aquí.</p>
    </div>
  );
}

const inputCls =
  "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";

// Pide la contraseña SL. El token que regresa el backend vive solo en memoria
// (estado de React): al recargar o salir de la pestaña se vuelve a pedir.
export function AccesoSL({ onAcceso, onCancelar }) {
  const [password, setPassword] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  // Mientras SL_PASSWORD no esté configurada en el servidor, SL entra directo.
  const [revisando, setRevisando] = useState(true);
  const onAccesoRef = useRef(onAcceso);
  onAccesoRef.current = onAcceso;

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const estado = await api.get("/sl/estado");
        if (estado && estado.requiere_password === false) {
          const r = await api.post("/sl/acceso", {});
          if (vivo) onAccesoRef.current(r.token);
          return;
        }
      } catch (e) {
        // si falla, se pide la contraseña como siempre
      }
      if (vivo) setRevisando(false);
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const entrar = async (e) => {
    e.preventDefault();
    if (!password) return;
    setEnviando(true);
    setError("");
    try {
      const r = await api.post("/sl/acceso", { password });
      onAcceso(r.token);
    } catch (err) {
      const msg = String(err.message || "");
      const detalle = /"detail":"([^"]+)"/.exec(msg)?.[1];
      setError(
        msg.includes("HTTP 503")
          ? "El acceso SL no está configurado en el servidor."
          : detalle || "No se pudo validar. Intenta de nuevo."
      );
      setPassword("");
      setEnviando(false);
    }
  };

  if (revisando) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-3"
      onClick={() => !enviando && onCancelar()}
      data-testid="sl-modal"
    >
      <form onSubmit={entrar} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3 my-4">
        <p className="text-sm font-bold text-slate-800">Clientes SL · remisión</p>
        <p className="text-xs text-slate-500">Escribe la contraseña para ver la cartera de remisión.</p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          autoComplete="off"
          data-testid="sl-password"
          className={inputCls}
          placeholder="Contraseña"
        />
        {error && <p className="text-xs text-rose-600 font-medium">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancelar} disabled={enviando} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
          <button type="submit" disabled={enviando || !password} data-testid="sl-entrar" className="text-xs font-semibold px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
            {enviando ? "Validando…" : "Entrar"}
          </button>
        </div>
      </form>
    </div>
  );
}

// Lista de clientes SL (remisión). Solo lectura y totalmente separada de la de factura.
function ClientesSL({ token, onSalir, onVencido }) {
  const [clientes, setClientes] = useState([]);
  const [saldoAl, setSaldoAl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState("saldo");
  const [abierto, setAbierto] = useState(null);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const data = await api.get("/sl/clientes", { "X-SL-Token": token });
        if (!vivo) return;
        setClientes(Array.isArray(data?.clientes) ? data.clientes : []);
        setSaldoAl(data?.saldo_al || null);
      } catch (e) {
        if (vivo) onVencido(); // token vencido o inválido: vuelve a pedir la contraseña
        return;
      }
      if (vivo) setLoading(false);
    })();
    return () => {
      vivo = false;
    };
  }, [token, onVencido]);

  const filtrados = useMemo(() => {
    const q = normalizar(busqueda.trim());
    const porNombre = (a, b) => normalizar(a.nombre).localeCompare(normalizar(b.nombre));
    const campo = { saldo: "saldo", vencido: "saldo_vencido", atraso: "dias_atraso_max" }[orden];
    const lista = [...clientes].sort((a, b) =>
      campo ? num(b[campo]) - num(a[campo]) || porNombre(a, b) : porNombre(a, b)
    );
    if (!q) return lista;
    return lista.filter((c) => [c.nombre, c.vendedor, c.id_estrategia].some((v) => normalizar(v).includes(q)));
  }, [clientes, busqueda, orden]);

  return (
    <div className="space-y-3 pb-8" data-testid="clientes-sl">
      <div className="bg-amber-50 rounded-2xl border border-amber-300 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Clientes SL · remisión</p>
          <p className="text-sm text-slate-600 mt-0.5">
            {clientes.length} {clientes.length === 1 ? "cliente" : "clientes"}
            {saldoAl && <span> · saldos al {fecha(saldoAl)} (Estrategia)</span>}
          </p>
        </div>
        <TotalesCartera lista={filtrados} filtrado={!!busqueda.trim()} />
        <button
          type="button"
          onClick={onSalir}
          data-testid="sl-salir"
          className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        >
          ← Clientes de factura
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex gap-2 flex-wrap sm:flex-nowrap">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="🔎 Buscar en clientes SL: nombre, vendedor, clave…"
          data-testid="sl-buscar"
          className={inputCls}
        />
        <select value={orden} onChange={(e) => setOrden(e.target.value)} className={`${inputCls} sm:w-60`}>
          {ORDENES.map((o) => (
            <option key={o.id} value={o.id}>Ordenar: {o.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                <th className="text-left px-3 py-2 font-semibold">Nombre</th>
                <th className="text-left px-3 py-2 font-semibold">Clave</th>
                <th className="text-left px-3 py-2 font-semibold">Vendedor</th>
                <th className="text-right px-3 py-2 font-semibold">Saldo</th>
                <th className="text-right px-3 py-2 font-semibold">Vencido</th>
                <th className="text-right px-3 py-2 font-semibold">Días de atraso</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((c) => (
                <tr
                  key={c.id_estrategia}
                  onClick={() => setAbierto(c)}
                  className="border-t border-slate-100 hover:bg-amber-50/60 cursor-pointer"
                  title="Clic para ver sus notas pendientes"
                >
                  <td className="px-3 py-2 font-medium text-slate-800">{c.nombre || "—"}</td>
                  <td className="px-3 py-2 font-mono">{c.id_estrategia}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{c.vendedor || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-slate-800">{mxn(c.saldo)}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${num(c.saldo_vencido) > 0 ? "text-rose-600 font-semibold" : ""}`}>
                    {mxn(c.saldo_vencido)}
                  </td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${num(c.dias_atraso_max) > 0 ? "text-rose-600" : ""}`}>
                    {num(c.dias_atraso_max)}
                  </td>
                </tr>
              ))}
              {!loading && filtrados.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-400 py-6">
                    {clientes.length === 0 ? "Aún no se han cargado clientes SL desde Estrategia." : "Ningún cliente SL coincide con la búsqueda."}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-400 py-6">Cargando…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {abierto && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
          onClick={() => setAbierto(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-4xl p-5 space-y-3 my-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold text-slate-800">{abierto.nombre || abierto.id_estrategia}</p>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">SL · remisión</span>
            </div>
            <CarteraDetalle c={abierto} titulo="Notas pendientes según Estrategia" />
            <div className="flex justify-end">
              <button type="button" onClick={() => setAbierto(null)} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [orden, setOrden] = useState("saldo");
  const [saldoAl, setSaldoAl] = useState(null);
  const [editando, setEditando] = useState(null); // objeto del formulario o null
  const [error, setError] = useState("");
  const [importMsg, setImportMsg] = useState("");
  // Vista SL: el token solo vive aquí (memoria); nunca en localStorage/sessionStorage.
  const [slToken, setSlToken] = useState(null);
  const [vistaSL, setVistaSL] = useState(false);
  const [pidiendoSL, setPidiendoSL] = useState(false);
  const slVencido = useCallback(() => {
    setSlToken(null);
    setVistaSL(false);
    setPidiendoSL(true);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/clientes");
        if (data && Array.isArray(data.clientes)) setClientes(data.clientes);
        if (data?.saldo_al) setSaldoAl(data.saldo_al);
      } catch (e) {
        // silencioso
      }
      setLoading(false);
    })();
  }, []);

  const persistir = async (lista) => {
    setGuardando(true);
    try {
      const saved = await api.post("/clientes", { clientes: lista });
      setClientes(Array.isArray(saved?.clientes) ? saved.clientes : lista);
      setGuardando(false);
      return true;
    } catch (e) {
      console.error("No se pudo guardar clientes:", e);
      alert("No se pudo guardar el cambio. Intenta de nuevo.");
      setGuardando(false);
      return false;
    }
  };

  const filtrados = useMemo(() => {
    const q = normalizar(busqueda.trim());
    const porNombre = (a, b) => normalizar(a.nombreComercial).localeCompare(normalizar(b.nombreComercial));
    const campo = { saldo: "saldo", vencido: "saldo_vencido", atraso: "dias_atraso_max" }[orden];
    const lista = [...clientes].sort((a, b) =>
      campo ? num(b[campo]) - num(a[campo]) || porNombre(a, b) : porNombre(a, b)
    );
    if (!q) return lista;
    return lista.filter((c) =>
      [c.clave, c.nombreComercial, c.razonSocial, c.rfc, c.contacto, c.telefono, c.correo, c.direccionEntrega, c.ciudad, c.estado, c.cp, c.notas, c.vendedor, c.id_estrategia]
        .some((v) => normalizar(v).includes(q))
    );
  }, [clientes, busqueda, orden]);

  const abrirNuevo = () => {
    setError("");
    setEditando({ ...VACIO });
  };
  const abrirEditar = (c) => {
    setError("");
    setEditando({ ...VACIO, ...c, condicionesPago: c.condicionesPago ?? "", limiteCredito: c.limiteCredito ?? "" });
  };
  const set = (campo) => (e) => setEditando((f) => ({ ...f, [campo]: e.target.value }));

  const guardar = async (e) => {
    e.preventDefault();
    const f = editando;
    const nombre = f.nombreComercial.trim();
    if (!nombre) return setError("El nombre comercial es obligatorio.");
    const rfc = f.rfc.trim().toUpperCase();
    if (rfc && !RFC_RE.test(rfc)) return setError("El RFC no tiene un formato válido (12 o 13 caracteres).");
    const correo = f.correo.trim();
    if (correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) return setError("Revisa el correo.");
    const limite = String(f.limiteCredito).replace(/[$,\s]/g, "");
    if (limite !== "" && (!isFinite(Number(limite)) || Number(limite) < 0))
      return setError("El límite de crédito debe ser un número mayor o igual a 0.");
    const duplicado = rfc && clientes.some((c) => c.id !== f.id && (c.rfc || "").toUpperCase() === rfc);
    if (duplicado) return setError("Ya hay otro cliente con ese RFC.");

    const ahora = new Date().toISOString();
    const registro = {
      ...f,
      id: f.id || nuevoId(),
      nombreComercial: nombre,
      razonSocial: f.razonSocial.trim(),
      rfc,
      contacto: f.contacto.trim(),
      telefono: f.telefono.trim(),
      correo,
      direccionEntrega: f.direccionEntrega.trim(),
      clave: String(f.clave || "").trim(),
      ciudad: String(f.ciudad || "").trim(),
      estado: String(f.estado || "").trim(),
      cp: String(f.cp || "").trim(),
      vendedor: String(f.vendedor || "").trim(),
      limiteCredito: limite === "" ? "" : Number(limite),
      notas: f.notas.trim(),
      creado: f.creado || ahora,
      actualizado: ahora,
    };
    const lista = f.id ? clientes.map((c) => (c.id === f.id ? registro : c)) : [...clientes, registro];
    if (await persistir(lista)) setEditando(null);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportMsg("");
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const nuevos = parseLibroClientes(wb);
      if (nuevos.length === 0) {
        setImportMsg("No encontré clientes en el archivo. Revisa que tenga una columna \"Razón social\".");
        return;
      }
      const { lista, agregados, actualizados } = combinarClientes(clientes, nuevos);
      if (agregados === 0 && actualizados === 0) {
        setImportMsg(`Los ${nuevos.length} clientes de "${file.name}" ya estaban dados de alta.`);
        return;
      }
      if (await persistir(lista)) {
        setImportMsg(
          `✅ "${file.name}": ${agregados} clientes nuevos` +
            (actualizados ? `, ${actualizados} existentes completados` : "") +
            (nuevos.length - agregados - actualizados ? `, ${nuevos.length - agregados - actualizados} sin cambios` : "") +
            "."
        );
      }
    } catch (err) {
      console.error("Error importando clientes:", err);
      setImportMsg("No se pudo leer el archivo. ¿Es un Excel (.xlsx)?");
    }
  };

  const eliminar = async () => {
    if (!editando?.id || editando.id_estrategia) return;
    if (!window.confirm(`¿Borrar a "${editando.nombreComercial}"? No se puede deshacer.`)) return;
    if (await persistir(clientes.filter((c) => c.id !== editando.id))) setEditando(null);
  };

  const labelCls = "block text-xs font-semibold text-slate-600 mb-1";

  const abrirSL = () => {
    if (slToken) setVistaSL(true);
    else setPidiendoSL(true);
  };

  const modalSL = pidiendoSL && (
    <AccesoSL
      onAcceso={(token) => {
        setSlToken(token);
        setPidiendoSL(false);
        setVistaSL(true);
      }}
      onCancelar={() => setPidiendoSL(false)}
    />
  );

  if (vistaSL && slToken) {
    return <ClientesSL token={slToken} onSalir={() => setVistaSL(false)} onVencido={slVencido} />;
  }

  return (
    <div className="space-y-3 pb-8">
      {modalSL}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Clientes de factura</p>
          <p className="text-sm text-slate-600 mt-0.5">
            {clientes.length} {clientes.length === 1 ? "cliente" : "clientes"}
            {saldoAl && <span> · saldos al {fecha(saldoAl)} (Estrategia)</span>}
            {guardando && <span className="text-teal-600"> · guardando…</span>}
          </p>
        </div>
        <TotalesCartera lista={filtrados} filtrado={!!busqueda.trim()} />
        <div className="flex gap-2 flex-wrap">
          <label
            data-testid="clientes-importar-btn"
            className={`text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-white border-2 border-dashed border-teal-400 text-teal-700 hover:bg-teal-50 ${
              guardando || loading ? "opacity-50 pointer-events-none" : "cursor-pointer"
            }`}
          >
            📁 Importar de Excel
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} data-testid="clientes-importar-input" />
          </label>
          <button
            type="button"
            onClick={abrirSL}
            data-testid="clientes-sl-btn"
            title="Clientes de remisión (pide contraseña)"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
          >
            🔒 SL
          </button>
          <button
            type="button"
            onClick={abrirNuevo}
            data-testid="clientes-nuevo-btn"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-slate-900 text-white hover:bg-slate-800"
          >
            + Nuevo cliente
          </button>
        </div>
        {importMsg && <p className="w-full text-xs text-slate-600" data-testid="clientes-import-msg">{importMsg}</p>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex gap-2 flex-wrap sm:flex-nowrap">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="🔎 Buscar: nombre, clave, RFC, contacto, ciudad, vendedor…"
          data-testid="clientes-buscar"
          className={inputCls}
        />
        <select
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          data-testid="clientes-orden"
          className={`${inputCls} sm:w-60`}
        >
          {ORDENES.map((o) => (
            <option key={o.id} value={o.id}>Ordenar: {o.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                <th className="text-left px-3 py-2 font-semibold">Clave</th>
                <th className="text-left px-3 py-2 font-semibold">Nombre comercial</th>
                <th className="text-left px-3 py-2 font-semibold">Razón social</th>
                <th className="text-left px-3 py-2 font-semibold">RFC</th>
                <th className="text-left px-3 py-2 font-semibold">Contacto</th>
                <th className="text-left px-3 py-2 font-semibold">Teléfono</th>
                <th className="text-left px-3 py-2 font-semibold">Correo</th>
                <th className="text-left px-3 py-2 font-semibold">Ciudad</th>
                <th className="text-left px-3 py-2 font-semibold">Pago</th>
                <th className="text-right px-3 py-2 font-semibold">Límite de crédito</th>
                <th className="text-left px-3 py-2 font-semibold">Vendedor</th>
                <th className="text-right px-3 py-2 font-semibold">Saldo</th>
                <th className="text-right px-3 py-2 font-semibold">Vencido</th>
                <th className="text-right px-3 py-2 font-semibold">Días de atraso</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => abrirEditar(c)}
                  data-testid={`cliente-row-${c.id}`}
                  className="border-t border-slate-100 hover:bg-teal-50/40 cursor-pointer"
                  title="Clic para editar"
                >
                  <td className="px-3 py-2 font-mono whitespace-nowrap">{c.clave || "—"}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">{c.nombreComercial}</td>
                  <td className="px-3 py-2">{c.razonSocial || "—"}</td>
                  <td className="px-3 py-2 font-mono">{c.rfc || "—"}</td>
                  <td className="px-3 py-2">{c.contacto || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{c.telefono || "—"}</td>
                  <td className="px-3 py-2">{c.correo || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{[c.ciudad, c.estado].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{condicionLabel(c.condicionesPago)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{mxn(c.limiteCredito)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{c.vendedor || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap font-semibold text-slate-800">
                    {c.id_estrategia ? mxn(c.saldo) : "—"}
                  </td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${num(c.saldo_vencido) > 0 ? "text-rose-600 font-semibold" : ""}`}>
                    {c.id_estrategia ? mxn(c.saldo_vencido) : "—"}
                  </td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${num(c.dias_atraso_max) > 0 ? "text-rose-600" : ""}`}>
                    {c.id_estrategia ? num(c.dias_atraso_max) : "—"}
                  </td>
                </tr>
              ))}
              {!loading && filtrados.length === 0 && (
                <tr>
                  <td colSpan={14} className="text-center text-slate-400 py-6">
                    {clientes.length === 0 ? "Aún no hay clientes. Da de alta el primero." : "Ningún cliente coincide con la búsqueda."}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={14} className="text-center text-slate-400 py-6">Cargando…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editando && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
          onClick={() => !guardando && setEditando(null)}
          data-testid="cliente-modal"
        >
          <form
            onSubmit={guardar}
            onClick={(e) => e.stopPropagation()}
            className={`bg-white rounded-2xl w-full ${editando.id_estrategia ? "max-w-4xl" : "max-w-2xl"} p-5 space-y-3 my-4`}
          >
            <p className="text-sm font-bold text-slate-800">{editando.id ? "Editar cliente" : "Nuevo cliente"}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nombre comercial *</label>
                <input className={inputCls} value={editando.nombreComercial} onChange={set("nombreComercial")} autoFocus data-testid="cliente-nombre" />
              </div>
              <div>
                <label className={labelCls}>Razón social</label>
                <input className={inputCls} value={editando.razonSocial} onChange={set("razonSocial")} />
              </div>
              <div>
                <label className={labelCls}>RFC</label>
                <input className={`${inputCls} uppercase font-mono`} value={editando.rfc} onChange={set("rfc")} maxLength={13} />
              </div>
              <div>
                <label className={labelCls}>Clave</label>
                <input className={`${inputCls} font-mono`} value={editando.clave} onChange={set("clave")} />
              </div>
              <div>
                <label className={labelCls}>Vendedor</label>
                <input className={inputCls} value={editando.vendedor} onChange={set("vendedor")} />
              </div>
              <div>
                <label className={labelCls}>Contacto</label>
                <input className={inputCls} value={editando.contacto} onChange={set("contacto")} />
              </div>
              <div>
                <label className={labelCls}>Teléfono</label>
                <input type="tel" className={inputCls} value={editando.telefono} onChange={set("telefono")} />
              </div>
              <div>
                <label className={labelCls}>Correo</label>
                <input type="email" className={inputCls} value={editando.correo} onChange={set("correo")} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Dirección de entrega</label>
                <textarea rows={2} className={inputCls} value={editando.direccionEntrega} onChange={set("direccionEntrega")} />
              </div>
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Ciudad</label>
                  <input className={inputCls} value={editando.ciudad} onChange={set("ciudad")} />
                </div>
                <div>
                  <label className={labelCls}>Estado</label>
                  <input className={inputCls} value={editando.estado} onChange={set("estado")} />
                </div>
                <div>
                  <label className={labelCls}>C.P.</label>
                  <input inputMode="numeric" className={inputCls} value={editando.cp} onChange={set("cp")} maxLength={5} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Condiciones de pago</label>
                <select className={inputCls} value={editando.condicionesPago} onChange={set("condicionesPago")} data-testid="cliente-condiciones">
                  {CONDICIONES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Límite de crédito (MXN)</label>
                <input inputMode="decimal" className={inputCls} value={editando.limiteCredito} onChange={set("limiteCredito")} placeholder="0.00" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notas</label>
                <textarea rows={3} className={inputCls} value={editando.notas} onChange={set("notas")} />
              </div>
            </div>

            {editando.id_estrategia && <CarteraDetalle c={editando} />}

            {error && <p className="text-xs text-rose-600 font-medium">{error}</p>}

            <div className="flex items-center justify-between gap-2 pt-1">
              <div>
                {editando.id && !editando.id_estrategia && (
                  <button type="button" onClick={eliminar} disabled={guardando} className="text-xs font-semibold px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50">
                    Borrar cliente
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditando(null)} disabled={guardando} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="submit" disabled={guardando} data-testid="cliente-guardar" className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
                  {guardando ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
